import {cellsToMultiPolygon, cellToBoundary} from 'h3-js';

export const convertToGeoJSONPolygon = (area: {
  id: string;
  isActivated: boolean;
  points: {lat: number; lng: number}[];
}) => ({
  type: 'Feature',
  geometry: {
    type: 'Polygon',
    coordinates: [[...area.points.map((p) => [p.lng, p.lat])]] // Map to [lng, lat]
  },
  properties: {
    id: area.id,
    isActivated: area.isActivated
  }
});

export const generateCirclePolygon = (
  center: [number, number],
  radiusInMeters: number,
  points = 64
) => {
  const coords: [number, number][] = [];
  const earthRadius = 6371000; // meters
  const [lng, lat] = center;

  for (let i = 0; i < points; i++) {
    const angle = (i * 360) / points;
    const radian = (angle * Math.PI) / 180;
    const dx = radiusInMeters * Math.cos(radian);
    const dy = radiusInMeters * Math.sin(radian);

    const deltaLng = dx / (earthRadius * Math.cos((lat * Math.PI) / 180) * (Math.PI / 180));
    const deltaLat = dy / (earthRadius * (Math.PI / 180));

    coords.push([lng + deltaLng, lat + deltaLat]);
  }

  coords.push(coords[0]); // Close the polygon

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [coords]
    },
    properties: {}
  };
};

/**
 * Converts an array of H3 cell IDs to polygon boundaries
 * @param h3CellIds - Array of H3 cell ID strings
 * @returns Array of polygon coordinates in format [[lat, lng], [lat, lng], ...]
 */
export const convertH3CellsToPolygons = (h3CellIds: string[] | null): number[][][] => {
  if (!h3CellIds || !Array.isArray(h3CellIds) || h3CellIds.length === 0) {
    return [];
  }

  try {
    return h3CellIds.map((cellId) => {
      const boundary = cellToBoundary(cellId); // Returns [[lat, lng], [lat, lng], ...]
      // Close the polygon by adding the first point at the end
      return [...boundary, boundary[0]];
    });
  } catch (error: any) {
    console.error('Error converting H3 cells to polygons:', error);
    return [];
  }
};

/**
 * Converts an array of H3 cell IDs to a single combined polygon boundary
 * Uses H3's cellsToMultiPolygon to combine cells into one or more polygons
 * @param h3CellIds - Array of H3 cell ID strings
 * @returns Array of polygon coordinates in format [[lat, lng], [lat, lng], ...]
 *          Returns the first polygon from the MultiPolygon result
 */
export const convertH3CellsToCombinedPolygon = (h3CellIds: string[] | null): number[][] | null => {
  if (!h3CellIds || !Array.isArray(h3CellIds) || h3CellIds.length === 0) {
    return null;
  }

  try {
    const multiPolygon = cellsToMultiPolygon(h3CellIds, true);

    if (!multiPolygon || !Array.isArray(multiPolygon) || multiPolygon.length === 0) {
      return null;
    }

    // Get the first polygon: multiPolygon[0]
    // Get the outer ring (first ring) of that polygon: multiPolygon[0][0]
    const firstRing = multiPolygon[0]?.[0];

    if (!firstRing || !Array.isArray(firstRing) || firstRing.length === 0) {
      return null;
    }

    // Convert from [lng, lat] to [lat, lng] format
    const polygon = firstRing.map((coord: [number, number]) => [coord[1], coord[0]]);

    // Close the polygon if not already closed
    if (
      polygon.length > 0 &&
      (polygon[0][0] !== polygon[polygon.length - 1][0] ||
        polygon[0][1] !== polygon[polygon.length - 1][1])
    ) {
      polygon.push(polygon[0]);
    }

    return polygon;
  } catch (error: any) {
    console.error('Error converting H3 cells to combined polygon:', error);
    return null;
  }
};

/**
 * Converts an array of H3 cell IDs to multiple polygon boundaries
 * Uses H3's cellsToMultiPolygon to combine cells into one or more polygons
 * Returns all closed polygons, even if hexagons are not all connected
 * @param h3CellIds - Array of H3 cell ID strings
 * @returns Array of polygon coordinates arrays, each in format [[lat, lng], [lat, lng], ...]
 */
export const convertH3CellsToMultiplePolygons = (h3CellIds: string[] | null): number[][][] => {
  if (!h3CellIds || !Array.isArray(h3CellIds) || h3CellIds.length === 0) {
    return [];
  }

  try {
    const multiPolygon = cellsToMultiPolygon(h3CellIds, true);

    if (!multiPolygon || !Array.isArray(multiPolygon) || multiPolygon.length === 0) {
      return [];
    }

    const polygons: number[][][] = [];

    // Process each polygon in the MultiPolygon
    multiPolygon.forEach((polygon) => {
      if (!polygon || !Array.isArray(polygon) || polygon.length === 0) {
        return;
      }

      // Get the outer ring (first ring) of each polygon
      const outerRing = polygon[0];
      if (!outerRing || !Array.isArray(outerRing) || outerRing.length === 0) {
        return;
      }

      // Convert from [lng, lat] to [lat, lng] format
      const convertedPolygon = outerRing.map((coord: [number, number]) => [coord[1], coord[0]]);

      // Close the polygon if not already closed
      if (
        convertedPolygon.length > 0 &&
        (convertedPolygon[0][0] !== convertedPolygon[convertedPolygon.length - 1][0] ||
          convertedPolygon[0][1] !== convertedPolygon[convertedPolygon.length - 1][1])
      ) {
        convertedPolygon.push(convertedPolygon[0]);
      }

      polygons.push(convertedPolygon);
    });

    return polygons;
  } catch (error: any) {
    console.error('Error converting H3 cells to multiple polygons:', error);
    return [];
  }
};
